import {
	httpClientIntegration,
	extraErrorDataIntegration,
	captureConsoleIntegration,
	browserTracingIntegration,
	replayIntegration,
	dedupeIntegration,
	init,
	reactRouterV7BrowserTracingIntegration
} from '@sentry/react';
import { excludeGraphQLFetch } from 'apollo-link-sentry';
import { useEffect } from 'react';
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType
} from 'react-router-dom';

import packageJson from '../../../package.json';
import { isAutomatedBrowser } from '../../utils/isAutomatedBrowser';

import { SentryEnv } from './SentryEnv';
import { getSentryDsn } from './utils/getSentryDsn';
import { getSentryEnv } from './utils/getSentryEnv';

export const initSentry = (): void => {
	const sentryDsn = getSentryDsn();
	const graphqlUri = import.meta.env.VITE_ODCO_GRAPHQL_BASE_URL;
	const sentryEnv = getSentryEnv();
	const tracePropagationTargets = ['localhost', new URL(graphqlUri).hostname];
	const automated = isAutomatedBrowser();

	init({
		enabled: import.meta.env.VITE_SENTRY_ENVIRONMENT !== SentryEnv.Local,
		dsn: sentryDsn,
		release: packageJson.version,
		environment: sentryEnv,
		beforeBreadcrumb: excludeGraphQLFetch,

		replaysOnErrorSampleRate: automated ? 0 : 1.0,
		tracesSampleRate: 0.01,

		tracePropagationTargets,

		ignoreErrors: [
			/Could not load models/,
			/json\.meshes is not iterable/,
			/Received status code 401/,
			/Object Not Found Matching Id/,
			/Failed to initialize WebGL/,
			/requires a style "glyphs" property/,
			/this\.errorCb is not a function/
		],

		integrations: [
			dedupeIntegration(),
			extraErrorDataIntegration(),
			captureConsoleIntegration({ levels: ['error'] }),
			browserTracingIntegration({ idleTimeout: 5 * 60 * 1000 }),
			httpClientIntegration({
				failedRequestStatusCodes: [
					[400, 599],
					[206, 206]
				]
			}),
			replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
				stickySession: true
			}),
			reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes
			})
		],

		beforeSend(event) {
			const isMapboxError =
				event.request?.url?.includes('mapbox.com') ||
				event.exception?.values?.some(value =>
					value.value?.includes('mapbox.com')
				);

			if (isMapboxError) {
				return null;
			}

			return event;
		}
	});
};
